import { useMemo, useState, useCallback } from 'react';

// Sentinels used to push nullish/non-comparable values to the bottom
// regardless of sort direction. We can't just lean on JS's default
// comparator because a `null < anything` is `false` *and* `null >
// anything` is `false`, which produces unstable ordering when null
// values are interleaved with real ones.
//
// Hoisted to module scope so identity is stable across renders and
// sort calls don't have to allocate a fresh sentinel.
const NULL_LO = Symbol('null-low');
const NULL_HI = Symbol('null-high');

function isNullish(v) {
  // We treat NaN as nullish too — a sortable column with NaN sprinkled
  // in (e.g. a `Number(undefined)` from upstream) should bucket those
  // rows the same way as missing values rather than degenerate to
  // undefined ordering.
  return v == null || (typeof v === 'number' && Number.isNaN(v));
}

// Best-effort coercion: numbers stay numbers, ISO/parseable date
// strings become epoch ms, everything else becomes a lowercased string
// for case-insensitive lexical ordering. We intentionally avoid
// over-cleverness like fuzzy currency parsing — callers can pass an
// explicit `keyExtractor` to compute whatever they want.
function coerce(v) {
  if (isNullish(v)) return null;
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (v instanceof Date) {
    const t = v.getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof v === 'string') {
    // Heuristic: only try Date.parse for strings that look like dates,
    // otherwise we'd accidentally parse "2024" as a year and break
    // alphabetical sort over a column of short identifiers.
    if (/^\d{4}-\d{2}-\d{2}/.test(v) || /^\d{1,2}\/\d{1,2}\/\d{2,4}/.test(v)) {
      const t = Date.parse(v);
      if (!Number.isNaN(t)) return t;
    }
    return v.toLowerCase();
  }
  return String(v).toLowerCase();
}

// Stable compare. For an ascending sort, nullish goes to the bottom;
// for descending, nullish *also* goes to the bottom (so users never
// see a screenful of "—" rows at the top of a desc sort).
export function compareValues(a, b, direction) {
  const an = isNullish(a);
  const bn = isNullish(b);
  if (an && bn) return 0;
  if (an) return 1;  // a is null, push down regardless of direction
  if (bn) return -1; // b is null, push down regardless of direction

  const ca = coerce(a);
  const cb = coerce(b);

  // After coercion either could still be nullish (e.g. an unparseable
  // Date object) — collapse to the same "push to bottom" handling.
  if (ca === null && cb === null) return 0;
  if (ca === null) return 1;
  if (cb === null) return -1;

  let cmp;
  if (typeof ca === 'number' && typeof cb === 'number') {
    cmp = ca - cb;
  } else {
    // Use localeCompare for natural-feeling ordering of mixed-content
    // strings (so "10" comes after "2" when numeric, but stays
    // alphabetical otherwise).
    cmp = String(ca).localeCompare(String(cb), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  }

  return direction === 'asc' ? cmp : -cmp;
}

/**
 * useSortableTable
 *
 * Returns { sortedRows, sortKey, sortDirection, requestSort } for an
 * in-memory rows array. Re-sorts on toggle; stable (preserves original
 * order for equal keys) by carrying the original index through the
 * comparator.
 *
 * @param {Array<object>} rows - rows to sort (left untouched).
 * @param {string|null} initialSortKey - column key to sort by initially.
 * @param {'asc'|'desc'} initialDirection - initial direction.
 * @param {Object<string, (row: object) => any>} [keyExtractors] -
 *   optional per-column extractors for computed columns (e.g. a
 *   "$/acre" derived from price/acres). Keys missing from this map
 *   fall back to `row[key]`.
 */
function useSortableTable(rows, initialSortKey = null, initialDirection = 'desc', keyExtractors = {}) {
  const [sortKey, setSortKey] = useState(initialSortKey);
  const [sortDirection, setSortDirection] = useState(initialDirection);

  const requestSort = useCallback((key) => {
    // Toggle direction on same column; switching to a new column resets
    // to descending — most "interesting" sorts (largest score, highest
    // price, longest DOM) read better top-down.
    setSortKey((prev) => {
      if (prev === key) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDirection('desc');
      return key;
    });
  }, []);

  const sortedRows = useMemo(() => {
    if (!Array.isArray(rows)) return rows;
    if (!sortKey) return rows;
    const extractor = keyExtractors[sortKey] || ((r) => r?.[sortKey]);

    // Decorate-sort-undecorate so the comparator can be stable without
    // requiring callers to give us a stable id per row. JS's
    // Array.prototype.sort is spec-stable as of ES2019 but the carried
    // index is belt-and-suspenders for older runtimes (CRA targets ES5
    // for older browsers via Babel).
    const indexed = rows.map((row, idx) => ({ row, idx, val: extractor(row) }));
    indexed.sort((a, b) => {
      const cmp = compareValues(a.val, b.val, sortDirection);
      return cmp !== 0 ? cmp : a.idx - b.idx;
    });
    return indexed.map(({ row }) => row);
  }, [rows, sortKey, sortDirection, keyExtractors]);

  return { sortedRows, sortKey, sortDirection, requestSort };
}

// Symbols re-exported in case a future caller wants explicit nullish
// handling (e.g. moving nulls to the top of an asc sort instead of the
// bottom). Not currently used internally.
export { NULL_LO, NULL_HI };

export default useSortableTable;
